import { NavLink, useLocation } from 'react-router-dom';
import { getStoredFanClaims } from '../auth/fan-auth';

/**
 * Mobile-first persistent bottom navigation bar — the "game HUD"
 * foundation. Hidden on >= sm where the top AppHeader's nav takes
 * over. Auto-hides on signed-out (matches the existing top-nav
 * behaviour of hiding 錢包 / 個人 when there's no fan to address) and
 * on the chrome-less /c/:id/chat route (the cloned EmbedRuntime
 * owns the full viewport there).
 *
 * Touch targets are h-14 (56px) — Apple HIG minimum is 44px; we go
 * larger because portrait-mobile thumbs have a wider miss radius
 * near the bottom edge. pb-[env(safe-area-inset-bottom)] keeps the
 * nav above the iOS home-indicator.
 */
type IconProps = { className?: string };

function HomeIcon({ className = '' }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M2.25 12L11.204 3.045c.439-.439 1.152-.439 1.591 0L21.75 12" />
      <path d="M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
    </svg>
  );
}

function BoxIcon({ className = '' }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5" />
      <path d="M10 11.25h4" />
      <path d="M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  );
}

function WalletIcon({ className = '' }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M21 12a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9" />
      <path d="M21 9a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
    </svg>
  );
}

function UserIcon({ className = '' }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function HeartIcon({ className = '' }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 21s-7-4.5-9.5-9C1 8 3 4 7 4c2 0 3.5 1 5 3 1.5-2 3-3 5-3 4 0 6 4 4.5 8C19 16.5 12 21 12 21z" />
    </svg>
  );
}

// 5-tab dock per fan-affinity-system (養成 added between Landing and
// Objects so the relationship surface sits next to the home feed —
// the natural "go check on my characters" flow.).
const items: Array<{ to: string; label: string; Icon: (p: IconProps) => JSX.Element; end?: boolean }> = [
  { to: '/', label: '首頁', Icon: HomeIcon, end: true },
  { to: '/relationships', label: '養成', Icon: HeartIcon, end: false },
  { to: '/objects', label: '物件庫', Icon: BoxIcon, end: true },
  { to: '/wallet', label: '錢包', Icon: WalletIcon, end: true },
  { to: '/me', label: '個人', Icon: UserIcon, end: true },
];

export function BottomNav() {
  const loc = useLocation();
  const signedIn = Boolean(getStoredFanClaims()?.sub);

  // Chat route renders edge-to-edge; the cloned EmbedRuntime there
  // draws its own back-button and owns the full viewport. Don't
  // overlay our nav on top.
  if (loc.pathname.includes('/chat')) return null;
  // Signed-out fans get nudged through the AppHeader sign-in button
  // on the landing page. The bottom dock is a "your stuff" dock, so
  // it only makes sense after a session exists.
  if (!signedIn) return null;

  return (
    <nav
      aria-label="主要導覽"
      className="fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-slate-800 bg-slate-900/95 backdrop-blur"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex">
        {items.map(({ to, label, Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                [
                  'group relative flex flex-col items-center justify-center gap-0.5 h-14',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-inset',
                  'transition-colors active:scale-[0.96]',
                  isActive ? 'text-violet-300' : 'text-slate-400 hover:text-slate-200',
                ].join(' ')
              }
              aria-label={label}
            >
              {({ isActive }) => (
                <>
                  {/* Active indicator — neon top-edge bar with soft glow.
                      Pure CSS, no layout shift on inactive tabs. */}
                  <span
                    aria-hidden="true"
                    className={[
                      'pointer-events-none absolute inset-x-3 top-0 h-[2px] rounded-full',
                      'bg-gradient-to-r from-violet-500 via-fuchsia-400 to-blue-500',
                      'transition-opacity duration-200',
                      isActive ? 'opacity-100 shadow-[0_0_10px_rgba(124,70,243,0.7)]' : 'opacity-0',
                    ].join(' ')}
                  />
                  <Icon
                    className={[
                      'h-6 w-6 transition-transform duration-200 ease-out',
                      isActive ? 'scale-110 -translate-y-0.5 drop-shadow-[0_0_6px_rgba(167,139,250,0.6)]' : '',
                    ].join(' ')}
                  />
                  <span className="text-[10px] font-medium leading-none">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
